/**
 * PIEPulseWidget — weekly learner pulse capture, bound to an active
 * commitment. Renders three states:
 *
 *   1. No active commitment → null (we don't nag if there's nothing
 *      to validate against; the dashboard's empty state already
 *      prompts the user toward the Improve flow).
 *   2. Eligible to submit → expanded form with the 5 questions.
 *   3. Already submitted this week → tiny acknowledgement strip with
 *      a "see your numbers" affordance toward the Coach.
 *
 * Design notes
 *  • Only renders inside the Dashboard's normal layout, never as a
 *    modal — pulse capture is a recurring micro-ritual, not an
 *    interrupt.
 *  • Submit is optimistic + replaces local state; no full-page
 *    refresh.
 *  • Friction tags are an explicit closed-vocabulary set so
 *    aggregation in the trend dashboard (M3) stays clean.
 */
import React, { useEffect, useState } from 'react';
import { Heart, Send, AlertTriangle, CheckCircle2, MessageCircleHeart } from 'lucide-react';
import { Link } from 'react-router-dom';
import { pieApi } from './pieApi';
import logger from '../../lib/logger';

const FRICTION_OPTIONS = [
  { id: 'time', label: 'Not enough time' },
  { id: 'clarity', label: "Wasn't clear what to do" },
  { id: 'tools', label: 'Tools / setup got in the way' },
  { id: 'support', label: 'Lacked support / coaching' },
];

const SCALE_LABELS = {
  behavior_application: { 1: 'Not at all', 3: 'Some of the time', 5: 'Consistently' },
  perceived_effectiveness: { 1: 'No change', 3: 'Some lift', 5: 'Big shift' },
  confidence: { 1: 'Shaky', 3: 'Steady', 5: 'Rock-solid' },
};

export default function PIEPulseWidget() {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    pieApi.pulseCurrent()
      .then(setState)
      .catch((e) => logger.warn('pulseCurrent failed', e));
  }, []);

  if (!state) return null; // initial fetch in flight or failed silently
  if (state.prompt === false && state.reason === 'no_commitment') return null;

  if (state.reason === 'already_submitted') {
    return <PulseAckStrip state={state} />;
  }

  return <PulsePromptCard state={state} busy={busy} setBusy={setBusy} setState={setState} />;
}

function PulseAckStrip({ state }) {
  const r = state.responses;
  return (
    <div
      className="pie-card"
      style={{
        marginBottom: 24, padding: '14px 18px',
        display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap',
        borderLeft: '3px solid var(--pie-success, #16A34A)',
      }}
      data-testid="pie-pulse-ack"
    >
      <CheckCircle2 size={18} style={{ color: 'var(--pie-success, #16A34A)', flexShrink: 0 }} aria-hidden />
      <div style={{ flex: 1, minWidth: 220 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--pie-text)' }}>
          You logged this week&apos;s pulse — nice.
        </div>
        <div style={{ fontSize: 12, color: 'var(--pie-text-secondary)', marginTop: 2 }}>
          Behavior <strong>{r.behavior_application}/5</strong> · Effectiveness <strong>{r.perceived_effectiveness}/5</strong> · Confidence <strong>{r.confidence}/5</strong>
          {r.friction && (
            <> · <span style={{ color: '#B45309' }}>friction: {(r.friction_tags || []).join(', ') || 'flagged'}</span></>
          )}
        </div>
      </div>
      <Link
        to="/mirror/coach"
        className="pie-btn pie-btn-ghost"
        data-testid="pie-pulse-coach-link"
        style={{ flexShrink: 0 }}
      >
        <MessageCircleHeart size={13} /> Talk it over
      </Link>
    </div>
  );
}

function PulsePromptCard({ state, busy, setBusy, setState }) {
  const [behaviorApplication, setBA] = useState(3);
  const [perceivedEffectiveness, setPE] = useState(3);
  const [confidence, setConfidence] = useState(3);
  const [friction, setFriction] = useState(false);
  const [frictionTags, setFrictionTags] = useState([]);
  const [reflection, setReflection] = useState('');

  const toggleTag = (id) => {
    setFrictionTags((curr) =>
      curr.includes(id) ? curr.filter((t) => t !== id) : [...curr, id]
    );
  };

  const onSubmit = async () => {
    setBusy(true);
    try {
      await pieApi.pulseSubmit({
        commitment_id: state.commitment.id,
        behavior_application: behaviorApplication,
        perceived_effectiveness: perceivedEffectiveness,
        confidence,
        friction,
        friction_tags: friction ? frictionTags : [],
        reflection: reflection.trim() || null,
      });
      // Refetch to flip into ack state.
      const next = await pieApi.pulseCurrent();
      setState(next);
    } catch (e) {
      logger.error('pulseSubmit failed', e);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="pie-card pie-card-elevated"
      style={{ marginBottom: 24, padding: '20px 22px' }}
      data-testid="pie-pulse-prompt"
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
        <Heart size={16} style={{ color: 'var(--pie-accent)' }} aria-hidden />
        <div className="pie-eyebrow" style={{ background: 'transparent', padding: 0, color: 'var(--pie-accent)' }}>
          Weekly pulse · {state.week_iso}
        </div>
      </div>
      <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--pie-text)', marginBottom: 4 }}>
        How did it go with{' '}
        <span style={{ color: 'var(--pie-accent)' }}>
          “{state.commitment.action_label}”
        </span>
        ?
      </div>
      <div style={{ fontSize: 12, color: 'var(--pie-text-secondary)', marginBottom: 18 }}>
        90 seconds. Honest answers feed the math behind your ROE.
      </div>

      <ScaleRow
        testid="pulse-behavior-application"
        label="I applied what I committed to"
        value={behaviorApplication}
        onChange={setBA}
        anchors={SCALE_LABELS.behavior_application}
      />
      <ScaleRow
        testid="pulse-perceived-effectiveness"
        label="My actions improved outcomes"
        value={perceivedEffectiveness}
        onChange={setPE}
        anchors={SCALE_LABELS.perceived_effectiveness}
      />
      <ScaleRow
        testid="pulse-confidence"
        label="I feel confident applying this"
        value={confidence}
        onChange={setConfidence}
        anchors={SCALE_LABELS.confidence}
      />

      <div
        style={{
          marginTop: 18, paddingTop: 16, borderTop: '1px solid var(--pie-border)',
        }}
      >
        <label
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            fontSize: 13, color: 'var(--pie-text)', cursor: 'pointer',
          }}
        >
          <input
            type="checkbox"
            checked={friction}
            onChange={(e) => setFriction(e.target.checked)}
            data-testid="pulse-friction-toggle"
          />
          <AlertTriangle size={13} style={{ color: '#B45309' }} aria-hidden />
          Anything got in your way?
        </label>
        {friction && (
          <div
            style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}
            data-testid="pulse-friction-tags"
          >
            {FRICTION_OPTIONS.map((opt) => {
              const active = frictionTags.includes(opt.id);
              return (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => toggleTag(opt.id)}
                  data-testid={`pulse-friction-tag-${opt.id}`}
                  style={{
                    padding: '6px 12px', borderRadius: 999, fontSize: 12,
                    border: `1px solid ${active ? 'var(--pie-accent)' : 'var(--pie-border)'}`,
                    background: active ? 'var(--pie-accent-soft, rgba(124, 92, 255, 0.10))' : 'transparent',
                    color: active ? 'var(--pie-accent)' : 'var(--pie-text-secondary)',
                    cursor: 'pointer', fontWeight: active ? 600 : 400,
                  }}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        )}
      </div>

      <div style={{ marginTop: 16 }}>
        <label
          htmlFor="pulse-reflection"
          style={{ fontSize: 12, color: 'var(--pie-text-secondary)', display: 'block', marginBottom: 6 }}
        >
          One thing you did differently this week (optional)
        </label>
        <textarea
          id="pulse-reflection"
          value={reflection}
          onChange={(e) => setReflection(e.target.value)}
          placeholder="e.g. Sent the agenda 24h ahead so the 1:1 stayed focused."
          rows={2}
          data-testid="pulse-reflection"
          style={{
            width: '100%', padding: '10px 12px', borderRadius: 8,
            border: '1px solid var(--pie-border)', resize: 'vertical',
            fontSize: 13, color: 'var(--pie-text)', background: 'var(--pie-surface)',
            fontFamily: 'inherit',
          }}
        />
      </div>

      <div style={{ marginTop: 18, display: 'flex', justifyContent: 'flex-end' }}>
        <button
          type="button"
          onClick={onSubmit}
          disabled={busy}
          className="pie-btn pie-btn-primary"
          data-testid="pulse-submit-btn"
        >
          <Send size={13} /> {busy ? 'Sending…' : 'Log this week\u2019s pulse'}
        </button>
      </div>
    </div>
  );
}

function ScaleRow({ label, value, onChange, anchors, testid }) {
  return (
    <div style={{ marginTop: 14 }}>
      <div style={{ fontSize: 13, color: 'var(--pie-text)', marginBottom: 6 }}>{label}</div>
      <div
        style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}
        data-testid={testid}
      >
        {[1, 2, 3, 4, 5].map((n) => {
          const active = value === n;
          return (
            <button
              key={n}
              type="button"
              onClick={() => onChange(n)}
              data-testid={`${testid}-${n}`}
              style={{
                width: 36, height: 36, borderRadius: 8,
                border: `1px solid ${active ? 'var(--pie-accent)' : 'var(--pie-border)'}`,
                background: active ? 'var(--pie-accent)' : 'transparent',
                color: active ? '#fff' : 'var(--pie-text-secondary)',
                fontSize: 13, fontWeight: 600, cursor: 'pointer',
                transition: 'all 0.12s ease',
              }}
            >
              {n}
            </button>
          );
        })}
        <div style={{ marginLeft: 8, fontSize: 11, color: 'var(--pie-text-muted)', flex: 1, minWidth: 100 }}>
          {anchors[value] || ''}
        </div>
      </div>
    </div>
  );
}
